import { aggregate, evalSummary } from './query.js';
import { formatNumber } from './format.js';
import { setHtml } from './css.js';
import { evalAggregate } from './formula.js';
import { headerChains, placeRowLayout, esc, mlGroupSegments, headerGroupSegments, isCustomSummary, AGG_LABEL, colAggs, footerLines } from './grid-util.js';
export class GridHeader {
    // ═══════════════════════ 헤더·필터·합계 ═══════════════════════
    /** @internal */ renderHeader() {
        const ml = !!this.opts.rowLayout && this.cols.some(c => c.lh);
        const { chains, depth } = headerChains(this.opts.headerGroups);
        const rows = ml ? depth + this.lines : this.headRows; // 멀티로우: 그룹 머리글 단계 아래에 레이아웃 줄
        // 줄바꿈·세로 머리글이 있으면 그 높이에 맞춤
        let hh = this.opts.headerHeight;
        for (const c of this.cols) {
            if (c.special)
                continue;
            const t = c.def.header ?? c.id;
            if (c.def.headerVertical)
                hh = Math.max(hh, Math.min(160, [...t].length * 14 + 16));
            else if (c.def.headerWrap)
                hh = Math.max(hh, this.wrapLines(t, Math.max(10, c.width - 14 - (this.opts.columnFilterMenu !== false ? 16 : 0)), 4) * 15 + 10);
        }
        const filterH = this.opts.showFilterRow ? 28 * (ml ? this.lines : 1) : 0;
        this.head.style.height = hh * rows + filterH + 'px';
        const parts = [[], [], []];
        // 그룹 머리글: 단계마다, 패인별 연속 구간마다 하나씩 (컬럼 이동으로 떨어지면 구간을 나눔)
        for (let L = 0; L < depth; L++) {
            const groupOf = new Map();
            for (const [id, ch] of chains)
                if (ch[L])
                    groupOf.set(id, ch[L]);
            for (const s of ml ? mlGroupSegments(this.cols, groupOf) : headerGroupSegments(this.cols, groupOf))
                parts[s.pane].push(`<div class="og-hc og-hg${s.group.collapsible ? ' og-hgc' : ''}" data-level="${L}"${s.group.collapsible ? ` data-g="${esc(s.group.header)}" role="button" aria-expanded="${!this.grpCollapsed.has(s.group.header)}"` : ''} style="left:${s.left}px;width:${s.width}px;top:${L * hh}px;height:${hh}px">${s.group.collapsible ? `<span class="og-gtg">${this.grpCollapsed.has(s.group.header) ? '▸' : '▾'}</span>` : ''}${esc(s.group.header)}</div>`);
        }
        for (const c of this.cols) {
            const k = chains.get(c.id)?.length ?? 0; // 이 컬럼 위 그룹 단계 수 → 그 아래부터 끝 줄까지 세로 병합
            const top = c.lh ? (depth + c.lt) * hh : ml ? 0 : k * hh, h = c.lh ? c.lh * hh : hh * (rows - (ml ? 0 : k));
            const si = this.sortKeys.findIndex(k => k.col === c.id);
            const sortMark = si < 0 ? '' : `<span class="og-sort">${this.sortKeys[si].dir === 'asc' ? '▲' : '▼'}${this.sortKeys.length > 1 ? si + 1 : ''}</span>`;
            const hck = c.def.headerCheck && !c.special ? this.headerCheckState(c) : null;
            const label = c.special === 'check' ? `<span class="og-chk${this.allChecked() ? ' og-on' : ''}"></span>` : (hck ? `<span class="og-chk og-hck${hck === 'on' ? ' og-on' : hck === 'mixed' ? ' og-mixed' : ''}${this.opts.editable && this.columnAccess(c.def) === 'edit' ? '' : ' og-dis'}" role="checkbox" aria-checked="${hck === 'on' ? 'true' : hck === 'mixed' ? 'mixed' : 'false'}" aria-label="${esc(c.def.header ?? c.id)} 전체"></span>` : '') + esc(c.def.header ?? c.id);
            const req = c.def.required ? '<span class="og-req">*</span>' : '';
            const canMenu = this.opts.columnFilterMenu !== false && this.menuFilterable(c);
            const fb = canMenu ? `<span class="og-fb${this.menuFilters.has(c.id) ? ' og-fon' : ''}" data-ci="${c.ci}" title="${this.t('필터 (Alt+↓)')}" role="button" tabindex="0" aria-haspopup="dialog" aria-label="${esc(c.def.header ?? c.id)} 필터"></span>` : '';
            const ariaSort = si < 0 ? 'none' : this.sortKeys[si].dir === 'asc' ? 'ascending' : 'descending';
            parts[c.pane].push(`<div class="og-hc${c.def.sortable === false || c.special ? '' : ' og-sortable'}${canMenu ? ' og-hasfb' : ''}${c.def.headerVertical ? ' og-hvert' : c.def.headerWrap ? ' og-hwrap' : ''}" data-ci="${c.ci}" role="columnheader" aria-colindex="${c.ci + 1}"${c.special ? '' : ` aria-sort="${ariaSort}"`}${c.special === 'check' ? ' aria-label="' + this.t('전체 선택') + '"' : c.special === 'rownum' ? ' aria-label="' + this.t('행 번호') + '"' : ''}${c.def.headerTooltip ? ` title="${esc(c.def.headerTooltip)}"` : ''} style="left:${c.left}px;width:${c.width}px;top:${top}px;height:${h}px">${req}${label}${sortMark}${fb}${c.special ? '' : '<span class="og-rs"></span>'}</div>`);
            if (filterH) {
                const can = !c.special && c.def.filterable !== false;
                parts[c.pane].push(`<div class="og-fc" style="left:${c.left}px;width:${c.width}px;top:${hh * rows + (c.lt ?? 0) * 28}px${ml ? `;height:${c.lh ? 28 : filterH}px` : ''}">${can ? `<input class="og-fi" data-col="${esc(c.id)}" placeholder="${c.def.type === 'number' || this.ds?.cols[c.id]?.kind === 'num' ? '>=0' : this.t('검색')}" value="${esc(this.filterText.get(c.id) ?? '')}" aria-label="${esc(c.def.header ?? c.id)} 필터">` : ''}</div>`);
            }
        }
        setHtml(this.headPanes[0], parts[0].join(''));
        setHtml(this.headInner, parts[1].join(''));
        setHtml(this.headPanes[2], parts[2].join(''));
    }
    /** @internal */ renderFooter() {
        const show = !!this.opts.showFooter;
        this.foot.hidden = !show;
        if (!show)
            return;
        const parts = [[], [], []];
        // summaries 가 있으면 집계 종류별 여러 줄, 없으면 한 줄
        const lines = footerLines(this.cols.map(c => c.def)), fh = 28;
        const hasSum = (c) => !!(c.def.summary || c.def.summaries?.length || c.def.summaryExpr);
        // 멀티로우: 집계 셀이 있는 레이아웃 줄마다 푸터 한 줄 (같은 가로 위치의 두 줄 집계가 겹치지 않게), 집계 없는 셀은 그리지 않음
        const ml = !!this.opts.rowLayout && this.cols.some(c => c.lh);
        const mlRows = ml ? [...new Set(this.cols.filter(c => c.lh && hasSum(c)).map(c => c.lt))].sort((a, b) => a - b) : [];
        const sub = Math.max(1, mlRows.length);
        this.foot.style.height = (lines?.length ?? 1) * sub * fh + 'px';
        const overlapsSum = (c) => ml && this.cols.some(x => x !== c && x.pane === c.pane && hasSum(x) && x.left < c.left + c.width && c.left < x.left + x.width);
        const labelCol = this.cols.find(c => !hasSum(c) && (c.pane === 0 || !this.paneWidth[0]) && !overlapsSum(c));
        for (let li = 0; li < (lines?.length ?? 1); li++) {
            const kind = lines?.[li];
            for (const c of this.cols) {
                if (ml && c !== labelCol && !hasSum(c))
                    continue;
                const top = (li * sub + (ml && c.lh ? Math.max(0, mlRows.indexOf(c.lt)) : 0)) * fh;
                let text = '', cls = '';
                const list = colAggs(c.def);
                if (kind ? list.includes(kind) : c.def.summary || c.def.summaryExpr) {
                    text = kind ? this.aggText(c, this.view, kind) : this.summaryText(c, this.view);
                    cls = ' og-num';
                }
                else if (kind && li === 0 && (c.def.summaryExpr || isCustomSummary(c.def.summary))) {
                    text = this.summaryText(c, this.view);
                    cls = ' og-num';
                }
                else if (c === labelCol) {
                    text = kind ? this.t(AGG_LABEL[kind]) : this.opts.footerLabel;
                    cls = ' og-flabel';
                }
                parts[c.pane].push(`<div class="og-fcell${cls}" data-ci="${c.ci}" data-line="${esc(kind ?? '')}" style="left:${c.left}px;width:${c.width}px;top:${top}px;height:${fh}px">${esc(text)}</div>`);
            }
        }
        setHtml(this.footPanes[0], parts[0].join(''));
        setHtml(this.footInner, parts[1].join(''));
        setHtml(this.footPanes[2], parts[2].join(''));
    }
    /** 서버 모드 집계: 그룹 머리행 rows·전체 view → 서버가 준 값. 없으면 undefined (서버 모드에서는 빈 칸) */
    /** @internal */ srvAgg(c, rows, s) {
        const m = rows === this.view ? this.srvTotals.aggs : this.srvAggOf.get(rows);
        return m ? (m[c.id + ':' + s] ?? null) : undefined;
    }
    /** @internal */ aggText(c, rows, s) {
        rows = this.liveRows(rows); // 삭제 행 표시 중이면 삭제 행 뺌
        const v = this.srm ? this.srvAgg(c, rows, s) ?? null : c.def.value ? this.aggComputed(c, rows, s) : aggregate(this.ds, rows, c.id, s);
        if (v == null)
            return '';
        if (typeof v === 'number')
            return s === 'count' ? formatNumber(v, 0) : s === 'avg' ? formatNumber(v, c.def.decimals ?? 1) : this.displayText(c, v, -1);
        return String(v);
    }
    /** 푸터 우클릭: 그 컬럼의 그 줄 집계를 바꿈 (합계/평균/건수/최소/최대/없음) */
    /** @internal */ onFooterContext(e) {
        const cell = e.target.closest('.og-fcell');
        const c = cell && this.cols[+cell.dataset.ci];
        if (!c || c.special || c.def.summaryExpr || isCustomSummary(c.def.summary))
            return;
        e.preventDefault();
        const line = (cell.dataset.line || '');
        const cur = colAggs(c.def);
        const now = line ? (cur.includes(line) ? line : null) : (cur[0] ?? null);
        const set = (fn) => {
            if (c.def.summaries?.length && line) {
                let next = cur.map(k => (k === line ? fn : k)).filter((k) => !!k);
                if (fn && !cur.includes(line))
                    next = [...cur, fn];
                this.setColumnProperty(c.id, 'summaries', [...new Set(next)]);
            }
            else if (line && footerLines(this.cols.map(x => x.def)).length > 1) {
                // 여러 줄 보기에서 summaries 없는 컬럼: 이 줄에 넣음
                const next = cur.filter(k => k !== line);
                if (fn)
                    next.push(fn);
                this.setColumnProperty(c.id, 'summary', undefined);
                this.setColumnProperty(c.id, 'summaries', [...new Set(next)]);
            }
            else
                this.setColumnProperty(c.id, 'summary', fn ?? undefined);
            this.notifyLayout();
        };
        const fns = ['sum', 'avg', 'count', 'min', 'max'];
        this.showMenu([...fns.map(fn => ({ label: this.t(AGG_LABEL[fn]), checked: now === fn, action: () => set(fn) })), { sep: true }, { label: this.t('없음'), checked: !now, action: () => set(null) }], e.clientX, e.clientY);
    }
    /** @internal */ summaryText(c, rows) {
        rows = this.liveRows(rows);
        if (c.def.summaryExpr) {
            const v = evalAggregate(c.def.summaryExpr, this.ds, rows);
            return v == null ? '' : typeof v === 'number' ? formatNumber(v, c.def.decimals ?? (Number.isInteger(v) ? 0 : 1)) : String(v);
        }
        const s = c.def.summary;
        const v = this.summaryValue(c, rows);
        if (v == null)
            return '';
        if (typeof v === 'number' && typeof s === 'object')
            return formatNumber(v, s.decimals ?? c.def.decimals ?? (s.fn === 'weightedAvg' ? 1 : Number.isInteger(v) ? 0 : 2));
        if (typeof v === 'number')
            return s === 'count' ? formatNumber(v, 0) : s === 'avg' ? formatNumber(v, c.def.decimals ?? 1) : this.displayText(c, v, -1);
        return String(v);
    }
    /** 컬럼 summary 값 (집계 이름·가중 평균·사용자 함수·옛 (rows, ds) 함수) */
    /** @internal */ summaryValue(c, rows) {
        const s = c.def.summary;
        if (!s)
            return null;
        if (this.srm)
            return typeof s === 'string' ? this.srvAgg(c, rows, s) ?? null : null; // 서버 모드: 서버 집계만 (가중·사용자 집계는 없음)
        if (typeof s === 'function')
            return s(rows, this.ds);
        if (typeof s === 'object')
            return evalSummary(s, this.ds, rows, c.id, c.def.value ? r => this.valueOf(c, r) : undefined);
        return c.def.value ? this.aggComputed(c, rows, s) : aggregate(this.ds, rows, c.id, s);
    }
    /** @internal */ aggComputed(c, rows, fn) {
        let s = 0, n = 0, mn = Infinity, mx = -Infinity;
        for (let i = 0; i < rows.length; i++) {
            const v = Number(c.def.value(rows[i], this.ds));
            if (v !== v)
                continue;
            s += v;
            n++;
            mn = Math.min(mn, v);
            mx = Math.max(mx, v);
        }
        if (!n)
            return null;
        return fn === 'sum' ? s : fn === 'avg' ? s / n : fn === 'count' ? n : fn === 'min' ? mn : mx;
    }
    /** 머리글 체크 상태: 보이는 데이터 행 기준 on/off/mixed */
    /** @internal */ headerCheckState(c) {
        const { on, n } = this.headerCheckCount(c);
        return !n || !on ? 'off' : on === n ? 'on' : 'mixed';
    }
    /** @internal */ headerCheckCount(c) {
        let x = this.hckCache.get(c.id);
        const dirty = this.hckDirty.get(c.id);
        this.hckDirty.delete(c.id);
        if (!x || x.disp !== this.disp || x.def !== c.def || x.len !== this.ds.length) {
            const len = this.ds.length, inV = new Uint8Array(len), on = new Uint8Array(len);
            this.hckRecounts++;
            let cnt = 0, n = 0;
            for (let vi = 0; vi < this.disp.length; vi++) {
                const r = this.disp[vi];
                if (r < 0 || r >= len || inV[r])
                    continue;
                inV[r] = 1;
                n++;
                if (this.isChecked(c, this.ds.get(r, c.id))) {
                    on[r] = 1;
                    cnt++;
                }
            }
            x = { disp: this.disp, def: c.def, len, inV, on, cnt, n };
            this.hckCache.set(c.id, x);
        }
        else if (dirty)
            for (const r of dirty) {
                if (!x.inV[r])
                    continue;
                const v = this.isChecked(c, this.ds.get(r, c.id)) ? 1 : 0;
                if (v !== x.on[r]) {
                    x.on[r] = v;
                    x.cnt += v ? 1 : -1;
                }
            }
        return { on: x.cnt, n: x.n };
    }
    /** @internal */ updateHeaderChecks() {
        const cs = this.cols.filter(c => c.def.headerCheck && !c.special);
        if (!cs.length)
            return;
        const sig = this.ds.version + ':' + this.ver;
        if (sig === this.hckSig)
            return;
        this.hckSig = sig;
        for (const c of cs) {
            const el = this.head.querySelector(`.og-hc[data-ci="${c.ci}"] .og-hck`);
            if (!el)
                continue;
            const st = this.headerCheckState(c);
            el.classList.toggle('og-on', st === 'on');
            el.classList.toggle('og-mixed', st === 'mixed');
            el.setAttribute('aria-checked', st === 'on' ? 'true' : st === 'mixed' ? 'mixed' : 'false');
        }
    }
    /** 머리글 체크 누름: 모두 켜져 있으면 끄고, 아니면 켠다 (편집 가능한 보이는 행만, 실행 취소 한 번) */
    /** @internal */ toggleHeaderCheck(c) { this.setColumnChecked(c.id, this.headerCheckState(c) !== 'on'); }
    /** 보이는 행 전체의 체크 값 설정 (편집 가능한 행만). 바꾼 행 수 */
    setColumnChecked(col, on) {
        const c = this.cols.find(x => x.id === col && !x.special);
        if (!c || !this.opts.editable)
            return 0;
        const [vOn, vOff] = this.checkValues(c), ch = [];
        for (let vi = 0; vi < this.disp.length; vi++) {
            const r = this.disp[vi];
            if (r < 0)
                continue;
            const k = this.editorOf(c, r);
            if (k !== 'check' && k !== 'check3')
                continue;
            if (!this.canEditCol(c, r))
                continue;
            const v = on ? vOn : vOff;
            if (this.ds.get(r, c.id) !== v)
                ch.push({ row: r, col: c.id, value: v });
        }
        if (ch.length)
            this.setCellByUser(ch, on ? this.t('전체 체크') : this.t('전체 체크 해제'), 'check', { noBuffer: true });
        return ch.length;
    }
    /** @internal */ onHeaderMouseDown(e) {
        const t = e.target;
        if (!t.classList.contains('og-rs')) {
            this.startColumnDrag(e);
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const ci = +t.parentElement.dataset.ci;
        if (this.opts.rowLayout && this.cols[ci]?.lc != null) {
            this.startMlResize(e, this.cols[ci]);
            return;
        }
        this.resizing = { ci, x: e.clientX, w: this.cols[ci].width };
        const move = (ev) => {
            if (!this.resizing)
                return;
            const c = this.cols[this.resizing.ci];
            c.width = Math.max(c.def.minWidth ?? 30, Math.min(c.def.maxWidth ?? Infinity, this.resizing.w + ev.clientX - this.resizing.x));
            if (this.fitMode() !== 'none')
                this.flexFixed.add(c.id);
            c.def.width = c.width;
            this.recalcWidths();
            this.renderHeader();
            this.renderFooter();
            this.pool.forEach(p => { p.key = ''; p.cells.forEach(a => a.forEach(x => (x.ci = -1))); });
            this.render(true);
        };
        const up = () => { this.resizing = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); this.notifyLayout(); };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }
    /** 멀티로우 너비 조절: 셀 오른쪽 끝의 격자 열 너비(colWidths)를 바꿈 — 같은 열의 다른 줄 셀도 함께 */
    /** @internal */ startMlResize(e, c) {
        const k = c.lc + c.ls - 1, x0 = e.clientX, w0 = this.mlColW[k] ?? c.width;
        const move = (ev) => {
            const w = [...this.mlColW];
            w[k] = Math.max(c.def.minWidth ?? 30, Math.min(c.def.maxWidth ?? 2000, w0 + ev.clientX - x0));
            this.opts.rowLayout = { ...this.opts.rowLayout, colWidths: w };
            this.relayoutMl();
        };
        const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); this.notifyLayout(); };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }
    /** 멀티로우 너비만 바뀐 경우: 셀 위치만 다시 (행 풀 유지) */
    /** @internal */ relayoutMl() {
        const rl = this.opts.rowLayout, byId = new Map(this.opts.columns.map(d => [d.id, d]));
        const pl = placeRowLayout({ ...rl, cells: rl.cells.filter(x => this.cols.some(c => c.lc != null && c.id === x.col)) }, id => byId.get(id)?.width, id => { const p = byId.get(id)?.pinned; return p === true || p === 'left' ? 'left' : p === 'right' ? 'right' : undefined; });
        this.mlPW = pl.paneWidths;
        this.mlColW = pl.colWidths;
        for (const x of pl.cells) {
            const c = this.cols.find(c => c.lc != null && c.id === x.col);
            if (c) {
                c.pl = x.pl;
                c.gx = x.left;
                c.width = x.width;
            }
        }
        this.recalcWidths();
        this.renderHeader();
        this.renderFooter();
        this.pool.forEach(p => { p.key = ''; p.cells.forEach(a => a.forEach(x => (x.ci = -1))); });
        this.render(true);
    }
    /** 멀티로우 셀 자리 바꾸기 (머리글 끌어 놓기): 두 컬럼이 레이아웃 칸을 맞바꿈 */
    swapLayoutCells(a, b) {
        const rl = this.opts.rowLayout;
        if (!rl || a === b || !rl.cells.some(x => x.col === a) || !rl.cells.some(x => x.col === b))
            return false;
        this.opts.rowLayout = { ...rl, cells: rl.cells.map(x => (x.col === a ? { ...x, col: b } : x.col === b ? { ...x, col: a } : x)) };
        this.layout();
        this.rebuild();
        this.notifyLayout();
        return true;
    }
    /** 헤더 끌기: 다른 헤더 위 → 순서 변경, 그룹 패널 위 → 그룹 */
    /** @internal */ startColumnDrag(e) {
        const t = e.target;
        if (e.button !== 0 || t.classList.contains('og-fb') || t.classList.contains('og-fi') || t.closest('.og-chk'))
            return;
        const hc = t.closest('.og-hc[data-ci]');
        if (!hc)
            return;
        const src = this.cols[+hc.dataset.ci];
        if (!src || src.special || this.opts.customize === 'none')
            return;
        const sx = e.clientX, sy = e.clientY;
        let dragging = false;
        let target = null, toPanel = false;
        const rb = () => this.root.getBoundingClientRect();
        const move = (ev) => {
            if (!dragging) {
                if (Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 6)
                    return;
                dragging = true;
                this.ghost.textContent = src.def.header ?? src.id;
                this.ghost.hidden = false;
                this.root.classList.add('og-dragging');
            }
            const r = rb();
            this.ghost.style.left = ev.clientX - r.left + 10 + 'px';
            this.ghost.style.top = ev.clientY - r.top + 6 + 'px';
            const el = document.elementFromPoint(ev.clientX, ev.clientY);
            toPanel = !!el && !this.gpanel.hidden && this.gpanel.contains(el);
            this.gpanel.classList.toggle('og-gdrop', toPanel);
            const th = el?.closest('.og-hc[data-ci]');
            const tc = th ? this.cols[+th.dataset.ci] : null;
            if (!toPanel && tc && !tc.special && tc !== src) {
                const b = th.getBoundingClientRect();
                const after = ev.clientX > b.left + b.width / 2;
                target = { col: tc.id, after };
                this.colMark.hidden = false;
                const swap = !!this.opts.rowLayout && src.lc != null; // 멀티로우는 칸 맞바꾸기 → 대상 칸 테두리
                this.colMark.classList.toggle('og-cmswap', swap);
                this.colMark.style.left = (swap ? b.left : after ? b.right : b.left) - r.left - (swap ? 0 : 1) + 'px';
                this.colMark.style.width = swap ? b.width + 'px' : '';
                this.colMark.style.top = (swap ? b.top - r.top : this.head.offsetTop) + 'px';
                this.colMark.style.height = (swap ? b.height : this.head.offsetHeight) + 'px';
            }
            else {
                target = null;
                this.colMark.hidden = true;
            }
        };
        const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            if (!dragging)
                return;
            this.suppressHeaderClick = true;
            setTimeout(() => { this.suppressHeaderClick = false; });
            this.ghost.hidden = true;
            this.colMark.hidden = true;
            this.gpanel.classList.remove('og-gdrop');
            this.root.classList.remove('og-dragging');
            if (toPanel) {
                const chips = [...this.gpanel.querySelectorAll('.og-gchip')];
                this.addGroup(src.id, chips.length);
            }
            else if (target) {
                if (this.opts.rowLayout && src.lc != null)
                    this.swapLayoutCells(src.id, target.col);
                else
                    this.moveColumn(src.id, target.col, target.after);
            }
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }
    /** @internal */ onHeaderClick(e) {
        if (this.suppressHeaderClick)
            return;
        const t = e.target;
        if (t.classList.contains('og-rs') || t.classList.contains('og-fi'))
            return;
        if (t.classList.contains('og-fb')) {
            e.stopPropagation();
            this.openFilterMenu(this.cols[+t.dataset.ci].id);
            return;
        }
        const hc = t.closest('.og-hc');
        if (hc?.dataset.g != null) {
            this.toggleColumnGroup(hc.dataset.g);
            return;
        }
        if (!hc || hc.dataset.ci == null)
            return;
        const c = this.cols[+hc.dataset.ci];
        if (this.opts.onHeaderClick?.({ col: c.id, grid: this }) === false)
            return;
        if (t.closest('.og-hck')) {
            if (!t.closest('.og-dis'))
                this.toggleHeaderCheck(c);
            return;
        }
        if (c.special === 'check') {
            const all = this.allChecked();
            for (const r of this.view)
                all ? this.checked.delete(r) : this.checked.add(r);
            this.renderHeader();
            this.bump();
            this.opts.onCheckChange?.({ rows: this.getCheckedRows(), grid: this });
            return;
        }
        if (c.special || c.def.sortable === false || (c.def.value && !c.def.valueGetter))
            return;
        const i = this.sortKeys.findIndex(k => k.col === c.id);
        const cur = i >= 0 ? this.sortKeys[i].dir : null;
        const next = cur == null ? 'asc' : cur === 'asc' ? 'desc' : null;
        if (e.shiftKey) {
            if (i >= 0)
                next ? (this.sortKeys[i] = { col: c.id, dir: next }) : this.sortKeys.splice(i, 1);
            else
                this.sortKeys.push({ col: c.id, dir: 'asc' });
        }
        else
            this.sortKeys = next ? [{ col: c.id, dir: next }] : [];
        this.commitEdit();
        this.renderHeader();
        this.rebuild();
        this.notifyLayout();
    }
}
